import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Task, Comment, Priority, Status, Assignee } from './types';

export const taskBoardApiRef = createApiRef<TaskBoardApi>({
  id: 'plugin.task-board.service',
});

export interface TaskBoardApi {
  getTasks(): Promise<Task[]>;
  createTask(data: { title: string; description?: string; priority: Priority; status?: Status; assignees?: Assignee[] }): Promise<Task>;
  updateTask(id: string, data: Partial<Pick<Task, 'status' | 'title' | 'description' | 'priority' | 'position' | 'assignees'>>): Promise<Task>;
  deleteTask(id: string): Promise<void>;
  getComments(taskId: string): Promise<Comment[]>;
  addComment(taskId: string, content: string, author: string): Promise<Comment>;
  updateComment(taskId: string, commentId: string, content: string): Promise<Comment>;
}

export class TaskBoardClient implements TaskBoardApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async base() {
    return this.discoveryApi.getBaseUrl('task-board');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.base()}${path}`;
    const res = await this.fetchApi.fetch(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...init?.headers },
    });
    if (!res.ok) throw new Error(`Task Board API error: ${res.statusText}`);
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  async getTasks(): Promise<Task[]> {
    const data = await this.request<{ tasks: Task[] }>('/tasks');
    return data.tasks;
  }

  async createTask(data: { title: string; description?: string; priority: Priority; status?: Status; assignees?: Assignee[] }): Promise<Task> {
    const res = await this.request<{ task: Task }>('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.task;
  }

  async updateTask(id: string, data: Partial<Pick<Task, 'status' | 'title' | 'description' | 'priority' | 'position' | 'assignees'>>): Promise<Task> {
    const res = await this.request<{ task: Task }>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res.task;
  }

  async deleteTask(id: string): Promise<void> {
    await this.request<void>(`/tasks/${id}`, { method: 'DELETE' });
  }

  async getComments(taskId: string): Promise<Comment[]> {
    const data = await this.request<{ comments: Comment[] }>(`/tasks/${taskId}/comments`);
    return data.comments;
  }

  async addComment(taskId: string, content: string, author: string): Promise<Comment> {
    const res = await this.request<{ comment: Comment }>(`/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, author }),
    });
    return res.comment;
  }

  async updateComment(taskId: string, commentId: string, content: string): Promise<Comment> {
    const res = await this.request<{ comment: Comment }>(`/tasks/${taskId}/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
    return res.comment;
  }
}
